import { PagePlus, Settings } from 'iconoir-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { Button } from '@savgroup-front-common/core';
import { MenuButton, type MenuOption } from '@savgroup-front-common/core/src/atoms/button/MenuButton/MenuButton';
import { Input } from '@savgroup-front-common/core/src/atoms/Form';
import { Modal } from '@savgroup-front-common/core/src/atoms/modal';
import { SaveIcon } from '@savgroup-front-common/core/src/protons/icons';
import { toast } from '@savgroup-front-common/core/src/atoms/Toast';

import {
  DEFAULT_STOCK_TABLE_VIEW_ID,
  getSystemStockTableViewById,
  SYSTEM_STOCK_TABLE_VIEWS,
} from '../../constants/stockTableSystemViews';
import {
  cloneStockTableViewState,
  type StockTableView,
  type StockTableViewState,
} from '../../types/stockTableView.types';
import {
  loadCustomStockTableViews,
  saveCustomStockTableViews,
} from '../../utils/stockTableViewsStorage';
import {
  areStockTableViewStatesEqual,
  createCustomViewId,
  hasActiveStockFiltersOrSearch,
} from './stockTableViews.helpers';
import {
  $ManageViewsButton,
  $ModalField,
  $ModalLabel,
  $StockTableViewsRow,
  $ViewSelector,
  $ViewsManageItem,
  $ViewsManageItemActions,
  $ViewsManageList,
} from './StockTableViews.styles';

export interface StockTableViewsProps {
  searchQuery: string;
  selectedFilters: StockTableViewState['selectedFilters'];
  onApplyView: (state: StockTableViewState) => void;
}

const StockTableViews = ({ searchQuery, selectedFilters, onApplyView }: StockTableViewsProps) => {
  const [customViews, setCustomViews] = useState<StockTableView[]>(() => loadCustomStockTableViews());
  const [selectedViewId, setSelectedViewId] = useState(DEFAULT_STOCK_TABLE_VIEW_ID);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [saveMode, setSaveMode] = useState<'create' | 'update'>('create');

  const currentState = useMemo<StockTableViewState>(
    () => ({
      searchQuery,
      selectedFilters,
    }),
    [searchQuery, selectedFilters],
  );

  const allViews = useMemo(
    () => [...SYSTEM_STOCK_TABLE_VIEWS, ...customViews],
    [customViews],
  );

  const menuOptions = useMemo<MenuOption[]>(
    () =>
      allViews.map((view) => ({
        value: view.id,
        label: view.label,
      })),
    [allViews],
  );

  const selectedView = useMemo(
    () => allViews.find((view) => view.id === selectedViewId) ?? SYSTEM_STOCK_TABLE_VIEWS[0],
    [allViews, selectedViewId],
  );

  const isCustomView = !selectedView.isSystem;
  const isViewModified = !areStockTableViewStatesEqual(currentState, selectedView.state);
  const canCreateView = hasActiveStockFiltersOrSearch(currentState);

  useEffect(() => {
    saveCustomStockTableViews(customViews);
  }, [customViews]);

  const handleSwitchView = useCallback(
    (option: MenuOption) => {
      const systemView = getSystemStockTableViewById(option.value);
      const customView = customViews.find((item) => item.id === option.value);
      const view = systemView ?? (customView ? { ...customView, state: cloneStockTableViewState(customView.state) } : undefined);

      if (!view) {
        return;
      }

      setSelectedViewId(view.id);
      onApplyView(cloneStockTableViewState(view.state));
    },
    [customViews, onApplyView],
  );

  const handleOpenSaveModal = useCallback((mode: 'create' | 'update') => {
    setSaveMode(mode);
    setNewViewName(mode === 'update' ? selectedView.label : '');
    setIsSaveModalOpen(true);
  }, [selectedView.label]);

  const handleCloseSaveModal = useCallback(() => {
    setIsSaveModalOpen(false);
    setNewViewName('');
  }, []);

  const handleConfirmSaveView = useCallback(() => {
    const label = newViewName.trim();

    if (!label) {
      return;
    }

    const stateSnapshot = cloneStockTableViewState(currentState);

    if (saveMode === 'update' && isCustomView) {
      setCustomViews((prev) =>
        prev.map((view) =>
          view.id === selectedViewId ? { ...view, label, state: stateSnapshot } : view,
        ),
      );
      toast.success(`Vue « ${label} » mise à jour.`, { autoClose: 3000, showIcon: true });
    } else {
      const newView: StockTableView = {
        id: createCustomViewId(),
        label,
        state: stateSnapshot,
      };

      setCustomViews((prev) => [...prev, newView]);
      setSelectedViewId(newView.id);
      toast.success(`Vue « ${label} » enregistrée.`, { autoClose: 3000, showIcon: true });
    }

    handleCloseSaveModal();
  }, [
    currentState,
    handleCloseSaveModal,
    isCustomView,
    newViewName,
    saveMode,
    selectedViewId,
  ]);

  const handleDeleteCustomView = useCallback(
    (viewId: string) => {
      setCustomViews((prev) => prev.filter((view) => view.id !== viewId));

      if (selectedViewId === viewId) {
        const defaultView = getSystemStockTableViewById(DEFAULT_STOCK_TABLE_VIEW_ID)!;

        setSelectedViewId(defaultView.id);
        onApplyView(cloneStockTableViewState(defaultView.state));
      }

      toast.success('Vue supprimée.', { autoClose: 3000, showIcon: true });
    },
    [onApplyView, selectedViewId],
  );

  return (
    <>
      <$StockTableViewsRow>
        <$ViewSelector>
          <MenuButton
            options={menuOptions}
            secondary
            small
            fluid
            dataTestId="stock-table-view-dropdown"
            value={selectedViewId}
            onChange={handleSwitchView}
            searchPlaceHolderMessage="Rechercher une vue…"
            noResultsFoundMessage={{
              id: 'stock.table.views.noResults',
              defaultMessage: 'Aucune vue ne correspond à votre recherche',
            }}
          />
        </$ViewSelector>

        <$ManageViewsButton
          type="button"
          aria-label="Paramétrer les vues"
          title="Paramétrer les vues"
          onClick={() => setIsManageModalOpen(true)}
          data-testid="stock-table-views-manage"
        >
          <Settings width={18} height={18} />
        </$ManageViewsButton>

        {isCustomView && isViewModified && (
          <Button
            type={BUTTON_TYPES.BUTTON}
            primary
            hollow
            small
            icon={<SaveIcon />}
            onClick={() => handleOpenSaveModal('update')}
            dataTestId="stock-table-view-save"
          >
            Enregistrer
          </Button>
        )}

        {canCreateView && (isViewModified || !isCustomView) && (
          <Button
            type={BUTTON_TYPES.BUTTON}
            primary
            hollow
            small
            icon={<PagePlus width={16} height={16} />}
            onClick={() => handleOpenSaveModal('create')}
            dataTestId="stock-table-view-create"
          >
            Nouvelle vue
          </Button>
        )}
      </$StockTableViewsRow>

      <Modal isOpen={isSaveModalOpen} onClose={handleCloseSaveModal}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '1.125rem' }}>
          {saveMode === 'update' ? 'Enregistrer la vue' : 'Créer une vue'}
        </h3>
        <$ModalField>
          <$ModalLabel htmlFor="stock-view-name">Nom de la vue</$ModalLabel>
          <Input
            name="stock-view-name"
            value={newViewName}
            placeholder="Ex. Stock Nintendo — disponible"
            onChange={(event) => setNewViewName(event.target.value)}
          />
        </$ModalField>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <Button type={BUTTON_TYPES.BUTTON} tertiary onClick={handleCloseSaveModal}>
            Annuler
          </Button>
          <Button
            type={BUTTON_TYPES.BUTTON}
            primary
            onClick={handleConfirmSaveView}
            disabled={!newViewName.trim()}
          >
            {saveMode === 'update' ? 'Enregistrer' : 'Créer'}
          </Button>
        </div>
      </Modal>

      <Modal isOpen={isManageModalOpen} onClose={() => setIsManageModalOpen(false)}>
        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.125rem' }}>Vues du tableau stock</h3>
        <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: '#64748b' }}>
          Les vues enregistrent la recherche et les filtres actifs. Les vues système ne peuvent pas être
          supprimées.
        </p>
        <$ViewsManageList>
          {allViews.map((view) => (
            <$ViewsManageItem key={view.id}>
              <span>
                {view.label}
                {view.isSystem ? (
                  <span style={{ marginLeft: '0.35rem', color: '#94a3b8', fontSize: '0.75rem' }}>
                    (système)
                  </span>
                ) : null}
              </span>
              <$ViewsManageItemActions>
                <Button
                  type={BUTTON_TYPES.BUTTON}
                  tertiary
                  small
                  onClick={() => {
                    setIsManageModalOpen(false);
                    handleSwitchView({ value: view.id, label: view.label });
                  }}
                >
                  Appliquer
                </Button>
                {!view.isSystem && (
                  <Button
                    type={BUTTON_TYPES.BUTTON}
                    tertiary
                    small
                    onClick={() => handleDeleteCustomView(view.id)}
                  >
                    Supprimer
                  </Button>
                )}
              </$ViewsManageItemActions>
            </$ViewsManageItem>
          ))}
        </$ViewsManageList>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <Button type={BUTTON_TYPES.BUTTON} primary onClick={() => setIsManageModalOpen(false)}>
            Fermer
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default StockTableViews;
